import React, { useEffect, useRef, useState, memo } from 'react';
import { Card, Icon } from 'tea-component';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { TStreaming } from '@/pages/aigc/pages/Agent/Copilot/components/CopilotScrollBar';
import { MainContentCmp } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp';
import { ThinkModeTextCmp } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/ThinkModeTextCmp';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import Answer from '@/pages/aigc/assets/svg/miaobi-new-colored.svg';
import './index.scss';
import { setLocale, useIntl } from 'umi';
import { SwitchAgentAccordion } from '@/pages/aigc/components/SwitchAgentAccordion';
import { useAccordionData } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useAccordionData';
import { useStallByText } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useStallByText';
import { StreamingText } from '@/pages/aigc/components/StreamingText';
import {
  TokenTotalCmp,
} from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/TokenTotalCmp';
import { shouldSkipThinkCmpRender } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/thinkCmpMemo';
export type TThinkCmpProps = {
  value?: Array<{
    type: string;
    value?: unknown;
    new_agent?: string;
    input?: string;
    inputIndex?: number;
  }>;
  status?: TStreaming; // 添加status属性类型定义
  isLastQA?: boolean;
  streamingState?: string;
  thinkTimeSeconds?: number;
  mode?: string;
  answer?: Array<{ type: string; value?: unknown; }>;
  query?: string;
  messageId?: string;
};

export const ThinkCmp = memo(
  (props: TThinkCmpProps) => {
    const {
      status,
      value,
      isLastQA,
      streamingState,
      thinkTimeSeconds,
      query,
      mode,
      messageId,
    } = props;
    const { sessionId } = useSearchParamsAppoint();
    const isLoading = status === 'pending';
    const [isShowDetail, setIsShowDetail] = useState(isLoading);
    const { formatMessage } = useIntl();
    const timeRef = useRef<any>(null);
    const firstFlag = useRef(false);
    const processedThinkIds = useRef<Set<string>>(new Set()); // 新增：记录已处理的thinkId
    setLocale('zh-CN', false);
    // 专门处理switch_agent类型的埋点逻辑
    useEffect(() => {
      if (!value || !streamingState) return;
      value.forEach((item: any) => {
        if (
          item.type === 'switch_agent' &&
          !processedThinkIds.current.has(item.thinkId)
        ) {
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501011',
            buttonName: 'IWiki使用',
            extInfo: JSON.stringify({
              sessionId,
              message: query,
              agent: item?.new_agent || item?.previous_agent || '',
              from: 'iwiki' 
            }),
            typeId: 'aigc',
          });
          processedThinkIds.current.add(item.thinkId); // 标记为已处理
        }
        if (
          item.type === 'plan_json' &&
          !processedThinkIds.current.has(item.thinkId)
        ) {
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501013',
            buttonName: '游戏名称',
            extInfo: JSON.stringify({
              sessionId,
              message: item?.buried,
              from: 'iwiki' 
            }),
            typeId: 'aigc',
          });
          processedThinkIds.current.add(item.thinkId); // 标记为已处理
        }
      });
    }, [value, query, sessionId, streamingState]);

    // 只执行一次自动关闭
    useEffect(() => {
      if (streamingState && !isLoading  && !firstFlag.current) {
        timeRef.current = setTimeout(() => {
          setIsShowDetail(false);
          firstFlag.current = true;
        }, 2000);
      }
      return () => {
        clearTimeout(timeRef.current);
      };
    }, [isLastQA, isLoading, streamingState]);
    
    const { thinkTime, thinkModeBlock, plannerBlock, accordionData, trailingInlineItems, tokenTotalBlock } = useAccordionData({
      value,
      isLoading,
      streamingState,
      thinkTimeSeconds,
      messageId
    });
    const thinkStalled = useStallByText(value, streamingState, { scope: 'think' });

    return (
      <>
        <div className="dba-agent-cover-containe-iwiki-header">
          <img
            src={Answer}
            alt="answer icon"
            className="dba_agent_cover-iwiki"
          />
          <span>答案</span>
        </div>
        <div id={`reference${messageId}`} />
        <div className="dba-agent-cover-container dba-agent-cover-containe-iwiki">
          <Card.Body
            style={{
              padding: '0px',
            }}
          >
            <div
              className={classNames('dba-thinking', {
                ['dba-thinking-over']: !isLoading,
              })}
              onClick={() => {
                if (isLoading) return;
                setIsShowDetail(!isShowDetail);
              }}
            >
              <div style={{ marginRight: '5px' }}>
                <SvgIcon type="agent-think-ds" style={{ marginRight: '5px' }} />
                {formatMessage(
                  {
                    id: isLoading
                      ? 'dba_agent_thinking'
                      : `dba_agent_thinkover_${mode || 'default'}`,
                  },
                  { agentName: 'AI' },
                )}
                {!isLoading && thinkTime && (
                  <span>{` (${formatMessage({
                    id: 'dba_agent_time_spent',
                  })}${thinkTime})`}</span>
                )}
              </div>
              {isLoading && <Icon type="loading" />}
              {!isLoading && (
                <div className="dba-thinking-process">
                  <SvgIcon
                    type={isShowDetail ? 'point-top' : 'point-down'}
                  ></SvgIcon>
                </div>
              )}
            </div>
          </Card.Body>
          <Collapse
            className='dba-system-container'
            isOpen={isShowDetail}
          >
            {thinkModeBlock && (
              <ThinkModeTextCmp
                value={thinkModeBlock.value}
                streamingState={streamingState}
              />
            )}
            {plannerBlock && (
              <MainContentCmp
                value={plannerBlock.value}
                className={classNames('dba-think-content-left dba-think-content-planner dba-think-iwiki-content-left', {
                  'dba-think-content-no-top': thinkModeBlock
                })}
                streamingState={streamingState}
                messageId={messageId}
              />
            )}
            {(accordionData.length > 0 || trailingInlineItems.length > 0) && (
              <SwitchAgentAccordion
                dataSource={accordionData}
                trailingInlineItems={trailingInlineItems}
                messageId={messageId}
                defaultOpenFirst={streamingState === 'pending'}
                streamingState={streamingState}
                isLoading={isLoading}
                iconTypeOpen="point-top"
                iconTypeClose="point-down"
                enableSticky={false}
              />
            )}
            {isLoading && isLastQA && thinkStalled && (
              <StreamingText
                from="think"
                textColor="#838697"
                streamColor="#000000"
                fontSize="14px"
                duration={1.5}
                loop
                autoPlay
              />
            )}
            <TokenTotalCmp value={Number(tokenTotalBlock?.value)} />
          </Collapse>
        </div>
      </>
    );
  },
  shouldSkipThinkCmpRender,
);

ThinkCmp.displayName = 'ThinkCmp';
